import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  FileText,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import useLocale from '../contexts/LocaleContext';
import { questionsApi, facilityTypesApi } from '../lib/api-client';
import { hasPermission } from '../lib/permissions';
import { getCurrentUser } from '../hooks/useAuth';
import { cn } from '../lib/utils';
import type {
  HygieneQuestionDto,
  HygieneOptionDto,
  QuestionnaireSectionDto,
  FacilitySubcategoryDto,
  CreateHygieneQuestionDto,
  UpdateHygieneQuestionDto,
} from '../types/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type LangKey = 'en' | 'rw' | 'fr';

interface LocalOption {
  id?: string;
  option: string;
  optionKinyarwanda: string;
  optionFrench: string;
  marks: number;
  _dirty: boolean;
  _new: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function flattenQuestions(roots: HygieneQuestionDto[]): HygieneQuestionDto[] {
  const out: HygieneQuestionDto[] = [];
  const walk = (qs: HygieneQuestionDto[]) => {
    for (const q of qs) { out.push(q); if (q.subQuestions?.length) walk(q.subQuestions); }
  };
  walk(roots);
  return out;
}

function optionsToLocal(options: HygieneOptionDto[]): LocalOption[] {
  return options.map(o => ({
    id: o.id,
    option: o.option,
    optionKinyarwanda: o.optionKinyarwanda ?? '',
    optionFrench: o.optionFrench ?? '',
    marks: o.marks,
    _dirty: false,
    _new: false,
  }));
}

// ─── LangTabs ─────────────────────────────────────────────────────────────────

function LangTabs({ active, onChange }: { active: LangKey; onChange: (l: LangKey) => void }) {
  return (
    <div className='flex gap-0.5 bg-gray-100 rounded-lg p-0.5 w-fit'>
      {(['en', 'rw', 'fr'] as const).map(l => (
        <button
          key={l} type='button' onClick={() => onChange(l)}
          className={cn(
            'px-2.5 py-1 text-xs font-semibold rounded-md transition-all',
            active === l ? 'bg-white shadow text-gray-900' : 'text-gray-400 hover:text-gray-700'
          )}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function QuestionnaireDetailPage() {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const { dictionary } = useLocale();
  const user = getCurrentUser();
  const queryClient = useQueryClient();

  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [sectionEdit, setSectionEdit] = useState<QuestionnaireSectionDto | 'new' | null>(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [hName, setHName] = useState('');
  const [hDesc, setHDesc] = useState('');
  const [hIsCurrent, setHIsCurrent] = useState(false);

  const canEdit = hasPermission(user as any, 'ASSESSMENTS_QUESTIONNAIRES');
  const queryKey = ['questions', 'hygiene', 'questionnaire', questionnaireId] as const;

  const { data: questionnaire, isLoading } = useQuery({
    queryKey,
    queryFn: () => questionsApi.getHygieneQuestionnaire(questionnaireId!),
    enabled: !!questionnaireId && canEdit,
  });

  const { data: facilityTypeSubcategories = [] } = useQuery({
    queryKey: ['facility-type-subcategories', questionnaire?.facilityTypeId],
    queryFn: () => facilityTypesApi.listSubcategories(questionnaire!.facilityTypeId!),
    enabled: !!questionnaire?.facilityTypeId,
    staleTime: 5 * 60 * 1000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const updateQMutation = useMutation({
    mutationFn: (body: { name?: string; description?: string; isCurrent?: boolean }) =>
      questionsApi.updateHygieneQuestionnaire(questionnaireId!, body),
    onSuccess: () => { invalidate(); setEditingHeader(false); toast.success('Saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const createSectionMutation = useMutation({
    mutationFn: (body: any) => questionsApi.createQuestionnaireSection(questionnaireId!, body),
    onSuccess: () => { invalidate(); setSectionEdit(null); toast.success('Section added'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const updateSectionMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      questionsApi.updateQuestionnaireSection(id, body),
    onSuccess: () => { invalidate(); setSectionEdit(null); toast.success('Section updated'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const deleteSectionMutation = useMutation({
    mutationFn: (id: string) => questionsApi.deleteQuestionnaireSection(id),
    onSuccess: () => { invalidate(); toast.success('Section removed'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const orderedSections = useMemo(
    () => [...(questionnaire?.sections ?? [])].sort((a, b) => a.order - b.order),
    [questionnaire?.sections]
  );

  const skipOptionChoices = useMemo(() => {
    if (!questionnaire?.questions) return [];
    return flattenQuestions(questionnaire.questions).flatMap(q =>
      (q.options ?? []).map(o => ({
        optionId: o.id,
        label: `${q.question.length > 46 ? q.question.slice(0, 46) + '…' : q.question} → ${o.option}`,
      }))
    );
  }, [questionnaire?.questions]);

  const questionsBySection = useMemo(() => {
    const groups: Record<string, HygieneQuestionDto[]> = { _none: [] };
    for (const s of orderedSections) groups[s.id] = [];
    for (const q of (questionnaire?.questions ?? []).filter(q => !q.parentQuestionId)) {
      const key = q.sectionId ?? '_none';
      if (!groups[key]) groups[key] = [];
      groups[key].push(q);
    }
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return groups;
  }, [questionnaire?.questions, orderedSections]);

  if (!canEdit) return (
    <div className='p-6 max-w-4xl mx-auto'>
      <p className='text-gray-500'>Access denied.</p>
      <Link to='/questions' className='text-primary hover:underline mt-2 inline-block'>← Back</Link>
    </div>
  );

  if (isLoading || !questionnaire) return (
    <div className='p-6 max-w-4xl mx-auto flex justify-center py-24'>
      <div className='h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-primary' />
    </div>
  );

  const newQKey = (sectionId: string | null) => `new:${sectionId ?? '_none'}`;
  const rootCount = (questionnaire.questions ?? []).filter(q => !q.parentQuestionId).length;

  return (
    <div className='max-w-7xl mx-auto py-6  sm:px-6'>

      {/* ── Header ── */}
      <div className='flex items-start gap-3 mb-8'>
        <Link to='/questions' className='p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 shrink-0 mt-0.5'>
          <ArrowLeft className='h-5 w-5' />
        </Link>
        {editingHeader ? (
          <div className='flex-1 space-y-2'>
            <input
              autoFocus
              className='w-full text-lg font-bold border-b-2 border-primary bg-transparent outline-none pb-1'
              value={hName} onChange={e => setHName(e.target.value)}
              placeholder='Questionnaire name'
            />
            <input
              className='w-full text-sm text-gray-500 border-b border-gray-300 bg-transparent outline-none pb-1'
              value={hDesc} onChange={e => setHDesc(e.target.value)}
              placeholder='Description (optional)'
            />
            <label className='flex items-center gap-2 text-sm text-gray-600 cursor-pointer'>
              <input type='checkbox' className='rounded' checked={hIsCurrent} onChange={e => setHIsCurrent(e.target.checked)} />
              Set as current questionnaire for this facility type
            </label>
            <div className='flex gap-2 pt-1'>
              <button
                onClick={() => updateQMutation.mutate({ name: hName || undefined, description: hDesc || undefined, isCurrent: hIsCurrent })}
                disabled={updateQMutation.isPending}
                className='px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50'
              >
                {updateQMutation.isPending ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setEditingHeader(false)} className='px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50'>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className='flex-1 min-w-0'>
            <div className='flex items-start gap-2'>
              <div className='flex-1 min-w-0'>
                <h1 className='text-xl font-bold text-gray-900'>{questionnaire.name}</h1>
                {questionnaire.description && (
                  <p className='text-sm text-gray-500 mt-0.5'>{questionnaire.description}</p>
                )}
                <div className='flex items-center gap-2 mt-1 flex-wrap'>
                  <span className='text-xs text-gray-400'>
                    {rootCount} question{rootCount !== 1 ? 's' : ''}
                    {orderedSections.length > 0 && ` · ${orderedSections.length} section${orderedSections.length !== 1 ? 's' : ''}`}
                  </span>
                  {questionnaire.isCurrent && (
                    <span className='text-xs px-1.5 py-0.5 bg-green-100 text-green-800 rounded font-medium'>
                      {dictionary['questions.current'] ?? 'Current'}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => { setHName(questionnaire.name); setHDesc(questionnaire.description ?? ''); setHIsCurrent(questionnaire.isCurrent); setEditingHeader(true); }}
                className='p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 shrink-0'
              >
                <Pencil className='h-4 w-4' />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Reference Documents ── */}
      <QuestionnaireDocumentsPanel questionnaireId={questionnaireId!} />

      {/* ── Unsectioned questions ── */}
      {questionsBySection['_none'] !== undefined && (
        <QuestionGroup
          groupKey='_none'
          section={null}
          questions={questionsBySection['_none']}
          sections={orderedSections}
          activeCardId={activeCardId}
          setActiveCardId={setActiveCardId}
          skipOptionChoices={skipOptionChoices}
          questionnaireId={questionnaireId!}
          onInvalidate={invalidate}
          onEditSection={null}
          onDeleteSection={null}
          newQKey={newQKey}
          subcategories={facilityTypeSubcategories}
          dictionary={dictionary}
        />
      )}

      {/* ── Sections ── */}
      {orderedSections.map(section =>
        sectionEdit && sectionEdit !== 'new' && (sectionEdit as QuestionnaireSectionDto).id === section.id ? (
          <SectionEditor
            key={section.id}
            section={section}
            subcategories={facilityTypeSubcategories}
            isPending={updateSectionMutation.isPending}
            onSubmit={body => updateSectionMutation.mutate({ id: section.id, body })}
            onCancel={() => setSectionEdit(null)}
          />
        ) : (
          <QuestionGroup
            key={section.id}
            groupKey={section.id}
            section={section}
            questions={questionsBySection[section.id] ?? []}
            sections={orderedSections}
            activeCardId={activeCardId}
            setActiveCardId={setActiveCardId}
            skipOptionChoices={skipOptionChoices}
            questionnaireId={questionnaireId!}
            onInvalidate={invalidate}
            onEditSection={() => setSectionEdit(section)}
            onDeleteSection={() => {
              if (window.confirm('Remove this section? Questions will remain but lose their section assignment.'))
                deleteSectionMutation.mutate(section.id);
            }}
            newQKey={newQKey}
            subcategories={facilityTypeSubcategories}
            dictionary={dictionary}
          />
        )
      )}

      {/* ── Add / edit section ── */}
      <div className='mt-6'>
        {sectionEdit === 'new' ? (
          <SectionEditor
            section={null}
            subcategories={facilityTypeSubcategories}
            isPending={createSectionMutation.isPending}
            onSubmit={body => createSectionMutation.mutate(body)}
            onCancel={() => setSectionEdit(null)}
          />
        ) : (
          <button
            onClick={() => setSectionEdit('new')}
            className='w-full flex items-center justify-center gap-2 py-3 text-sm text-gray-400 hover:text-indigo-600 border-2 border-dashed border-gray-200 hover:border-indigo-300 rounded-xl transition-colors'
          >
            <Plus className='h-4 w-4' /> Add section
          </button>
        )}
      </div>
    </div>
  );
}

// ─── QuestionnaireDocumentsPanel ──────────────────────────────────────────────

function QuestionnaireDocumentsPanel({ questionnaireId }: { questionnaireId: string }) {
  const { dictionary } = useLocale();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');

  const queryKey = ['questions', 'questionnaire-documents', questionnaireId] as const;

  const { data: documents = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => questionsApi.listQuestionnaireDocuments(questionnaireId),
  });

  const uploadMutation = useMutation({
    mutationFn: () => questionsApi.uploadQuestionnaireDocument(questionnaireId, file!, label || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setFile(null);
      setLabel('');
      toast.success('Document uploaded');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Upload failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) => questionsApi.deleteQuestionnaireDocument(documentId),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey }); toast.success('Document removed'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  return (
    <div className='mb-8 border border-gray-200 rounded-xl p-4'>
      <h2 className='text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2'>
        <FileText className='h-4 w-4 text-primary' /> {dictionary['questionnaire.reference_documents']}
      </h2>

      {isLoading ? (
        <div className='text-sm text-gray-400'>Loading…</div>
      ) : documents.length === 0 ? (
        <p className='text-sm text-gray-400 mb-3'>{dictionary['questionnaire.no_documents']}</p>
      ) : (
        <ul className='divide-y divide-gray-100 mb-3'>
          {documents.map(doc => (
            <li key={doc.id} className='flex items-center justify-between py-2'>
              <a
                href={doc.asset.url}
                target='_blank'
                rel='noreferrer'
                className='text-sm text-primary hover:underline truncate flex-1'
              >
                {doc.label || doc.asset.originalName}
              </a>
              <span className='text-xs text-gray-400 mx-2 shrink-0'>
                {(doc.asset.size / 1024 / 1024).toFixed(1)} MB
              </span>
              <button
                onClick={() => {
                  if (window.confirm(dictionary['questionnaire.delete_document_confirm'])) deleteMutation.mutate(doc.id);
                }}
                className='p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 shrink-0'
              >
                <Trash2 className='h-4 w-4' />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className='flex flex-wrap items-center gap-2'>
        <input type='file' accept='application/pdf' onChange={e => setFile(e.target.files?.[0] ?? null)} className='text-sm' />
        <input
          type='text'
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder={dictionary['questionnaire.document_label']}
          className='text-sm border border-gray-200 rounded-md px-2 py-1'
        />
        <button
          onClick={() => uploadMutation.mutate()}
          disabled={!file || uploadMutation.isPending}
          className='px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50'
        >
          {uploadMutation.isPending ? 'Uploading…' : dictionary['questionnaire.upload_document']}
        </button>
      </div>
    </div>
  );
}

// ─── QuestionGroup ────────────────────────────────────────────────────────────

function QuestionGroup({
  groupKey, section, questions, sections, activeCardId, setActiveCardId,
  skipOptionChoices, questionnaireId, onInvalidate, onEditSection, onDeleteSection,
  newQKey, subcategories, dictionary,
}: {
  groupKey: string;
  section: QuestionnaireSectionDto | null;
  questions: HygieneQuestionDto[];
  sections: QuestionnaireSectionDto[];
  activeCardId: string | null;
  setActiveCardId: (id: string | null) => void;
  skipOptionChoices: { optionId: string; label: string }[];
  questionnaireId: string;
  onInvalidate: () => void;
  onEditSection: (() => void) | null;
  onDeleteSection: (() => void) | null;
  newQKey: (sectionId: string | null) => string;
  subcategories: FacilitySubcategoryDto[];
  dictionary: Record<string, string>;
}) {
  const nqk = newQKey(section?.id ?? null);
  const [collapsed, setCollapsed] = useState(false);

  if (!section && questions.length === 0 && activeCardId !== nqk) return null;

  return (
    <div className='space-y-2 mb-2'>
      {section ? (
        /* Section header */
        <div className='flex items-center gap-2 group mt-8 mb-2'>
          <button type='button' onClick={() => setCollapsed(c => !c)} className='text-gray-400 hover:text-gray-600 p-0.5'>
            {collapsed ? <ChevronRight className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
          </button>
          <div className='flex-1 min-w-0 border-b-2 border-indigo-400 pb-1 flex items-center gap-2'>
            <span className='font-bold text-indigo-700 text-base tracking-wide uppercase leading-none'>
              {section.title}
            </span>
            {section.description && (
              <span className='text-xs text-gray-400 font-normal truncate'>{section.description}</span>
            )}
          </div>
          <div className='flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0'>
            <span className='text-xs text-gray-400 mr-1'>{questions.length} q</span>
            {onEditSection && (
              <button onClick={onEditSection} title='Edit section'
                className='p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600'>
                <Pencil className='h-3.5 w-3.5' />
              </button>
            )}
            {onDeleteSection && (
              <button onClick={onDeleteSection} title='Delete section'
                className='p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500'>
                <Trash2 className='h-3.5 w-3.5' />
              </button>
            )}
          </div>
        </div>
      ) : (
        questions.length > 0 && (
          <div className='flex items-center gap-2 mb-3'>
            <span className='text-xs font-semibold text-gray-400 uppercase tracking-wider'>Unsectioned</span>
            <div className='flex-1 border-b border-gray-200' />
          </div>
        )
      )}

      {!collapsed && (
        <>
          {questions.map((q, idx) => (
            <QuestionCard
              key={q.id}
              question={q}
              index={idx + 1}
              sections={sections}
              skipOptionChoices={skipOptionChoices.filter(c =>
                !(q.options ?? []).some(o => o.id === c.optionId)
              )}
              questionnaireId={questionnaireId}
              isActive={activeCardId === q.id}
              onActivate={() => setActiveCardId(q.id)}
              onDeactivate={() => setActiveCardId(null)}
              onSaved={() => { setActiveCardId(null); onInvalidate(); }}
              onDeleted={() => { setActiveCardId(null); onInvalidate(); }}
            />
          ))}

          {activeCardId === nqk ? (
            <NewQuestionCard
              questionnaireId={questionnaireId}
              sectionId={section?.id ?? null}
              sections={sections}
              skipOptionChoices={skipOptionChoices}
              onSaved={() => { setActiveCardId(null); onInvalidate(); }}
              onCancel={() => setActiveCardId(null)}
            />
          ) : (
            <button
              onClick={() => setActiveCardId(nqk)}
              className='w-full flex items-center gap-2 py-2.5 px-4 text-sm text-gray-400 hover:text-indigo-600 border border-dashed border-gray-200 hover:border-indigo-300 rounded-xl transition-colors bg-white hover:bg-indigo-50/20'
            >
              <Plus className='h-4 w-4' />
              Add question{section ? ` to "${section.title}"` : ''}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── QuestionCard ─────────────────────────────────────────────────────────────

function QuestionCard({
  question, index, sections, skipOptionChoices, questionnaireId,
  isActive, onActivate, onDeactivate, onSaved, onDeleted,
}: {
  question: HygieneQuestionDto;
  index: number;
  sections: QuestionnaireSectionDto[];
  skipOptionChoices: { optionId: string; label: string }[];
  questionnaireId: string;
  isActive: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const queryKey = ['questions', 'hygiene', 'questionnaire', questionnaireId];

  const [lang, setLang] = useState<LangKey>('en');
  const [text, setText] = useState('');
  const [textRw, setTextRw] = useState('');
  const [textFr, setTextFr] = useState('');
  const [weight, setWeight] = useState(10);
  const [sectionId, setSectionId] = useState('');
  const [skipIfOptionId, setSkipIfOptionId] = useState('');
  const [localOptions, setLocalOptions] = useState<LocalOption[]>([]);
  const [addingSubFor, setAddingSubFor] = useState<HygieneOptionDto | null>(null);

  const enterEdit = () => {
    setText(question.question);
    setTextRw(question.questionKinyarwanda ?? '');
    setTextFr(question.questionFrench ?? '');
    setWeight(question.weight);
    setSectionId(question.sectionId ?? '');
    setSkipIfOptionId(question.skipIfOptionId ?? '');
    setLocalOptions(optionsToLocal(question.options ?? []));
    setAddingSubFor(null);
    onActivate();
  };

  const updateQMut = useMutation({
    mutationFn: (body: UpdateHygieneQuestionDto) => questionsApi.updateHygieneQuestion(question.id, body),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save'),
  });
  const deleteQMut = useMutation({
    mutationFn: () => questionsApi.deleteHygieneQuestion(question.id),
    onSuccess: () => { toast.success('Question deleted'); onDeleted(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });
  const createOptMut = useMutation({
    mutationFn: (body: any) => questionsApi.createHygieneOption(question.id, body),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });
  const updateOptMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => questionsApi.updateHygieneOption(id, body),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });
  const deleteOptMut = useMutation({
    mutationFn: (id: string) => questionsApi.deleteHygieneOption(id),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });
  const createSubQMut = useMutation({
    mutationFn: (body: CreateHygieneQuestionDto) => questionsApi.createHygieneQuestion(questionnaireId, body),
    onSuccess: () => {
      toast.success('Sub-question added');
      setAddingSubFor(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const handleSave = async () => {
    if (!text.trim()) { toast.error('Question text is required'); return; }
    try {
      await updateQMut.mutateAsync({
        question: text.trim(),
        questionKinyarwanda: textRw.trim() || undefined,
        questionFrench: textFr.trim() || undefined,
        weight,
        sectionId: sectionId || null,
        skipIfOptionId: skipIfOptionId || null,
      });
      for (const opt of localOptions) {
        if (opt._new && opt.option.trim()) {
          await createOptMut.mutateAsync({
            option: opt.option.trim(),
            optionKinyarwanda: opt.optionKinyarwanda.trim() || undefined,
            optionFrench: opt.optionFrench.trim() || undefined,
            marks: opt.marks,
          });
        } else if (!opt._new && opt._dirty && opt.id) {
          await updateOptMut.mutateAsync({
            id: opt.id,
            body: {
              option: opt.option.trim(),
              optionKinyarwanda: opt.optionKinyarwanda.trim() || undefined,
              optionFrench: opt.optionFrench.trim() || undefined,
              marks: opt.marks,
            },
          });
        }
      }
      toast.success('Saved');
      onSaved();
    } catch { /* each mutation shows its own toast */ }
  };

  const handleDeleteOption = async (idx: number) => {
    const opt = localOptions[idx];
    if (opt._new) { setLocalOptions(prev => prev.filter((_, i) => i !== idx)); return; }
    if (!window.confirm('Delete this option?')) return;
    await deleteOptMut.mutateAsync(opt.id!);
    setLocalOptions(prev => prev.filter((_, i) => i !== idx));
    queryClient.invalidateQueries({ queryKey });
  };

  const isSaving = updateQMut.isPending || createOptMut.isPending || updateOptMut.isPending;

  /* ── Collapsed ── */
  if (!isActive) {
    return (
      <div
        onClick={enterEdit}
        className='group border border-gray-200 rounded-xl p-4 bg-white cursor-pointer hover:border-indigo-200 hover:shadow-sm transition-all'
      >
        <div className='flex items-start gap-3'>
          <span className='text-sm font-bold text-gray-300 w-5 shrink-0 pt-0.5 select-none'>{index}</span>
          <div className='flex-1 min-w-0'>
            <p className='text-base font-medium text-gray-900 leading-snug'>{question.question}</p>
            <div className='flex items-center gap-2 mt-1.5 flex-wrap'>
              <span className='text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-md'>wt {question.weight}</span>
              {(question.options?.length ?? 0) > 0 && (
                <span className='text-xs text-gray-400'>{question.options!.length} options</span>
              )}
              {(question.subQuestions?.length ?? 0) > 0 && (
                <span className='text-xs px-2 py-0.5 bg-purple-50 text-purple-600 rounded-md'>
                  {question.subQuestions!.length} sub-q
                </span>
              )}
              {question.skipIfOptionId && (
                <span className='text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded-md'>conditional</span>
              )}
              {question.sectionId && sections.find(s => s.id === question.sectionId) && (
                <span className='text-xs px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-md'>
                  {sections.find(s => s.id === question.sectionId)!.title}
                </span>
              )}
            </div>
          </div>
          <div className='flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0'>
            <button onClick={e => { e.stopPropagation(); enterEdit(); }} title='Edit'
              className='p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700'>
              <Pencil className='h-3.5 w-3.5' />
            </button>
            <button
              onClick={e => { e.stopPropagation(); if (window.confirm('Delete this question and all its options?')) deleteQMut.mutate(); }}
              title='Delete'
              className='p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500'
            >
              <Trash2 className='h-3.5 w-3.5' />
            </button>
          </div>
        </div>

        {/* Options preview */}
        {(question.options?.length ?? 0) > 0 && (
          <div className='mt-2.5 ml-8 space-y-1.5'>
            {question.options!.map(opt => (
              <div key={opt.id} className='flex items-center gap-2'>
                <span className='w-3 h-3 rounded-full border-2 border-gray-300 shrink-0' />
                <span className='text-base text-gray-600 flex-1 truncate'>{opt.option}</span>
                <span className='text-xs text-gray-400 shrink-0'>{opt.marks} pts</span>
                {(question.subQuestions ?? []).some(sq => sq.triggerOptionId === opt.id) && (
                  <span className='text-xs text-purple-400 shrink-0'>↳ sub-q</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ── Expanded (edit mode) ── */
  return (
    <div className='border-2 border-indigo-300 rounded-xl bg-white shadow-md'>
      {/* Top bar */}
      <div className='px-4 pt-4 pb-2 flex items-center justify-between'>
        <LangTabs active={lang} onChange={setLang} />
        <span className='text-xs font-semibold text-indigo-400'>Q{index}</span>
      </div>

      {/* Question text */}
      <div className='px-4 pb-3'>
        <textarea
          rows={2}
          autoFocus={lang === 'en'}
          className='w-full text-base font-medium border-0 border-b-2 border-indigo-200 focus:border-indigo-500 outline-none resize-none bg-transparent pb-1 transition-colors placeholder:text-gray-300'
          placeholder={
            lang === 'en' ? 'Question text (English)…' :
            lang === 'rw' ? 'Ibibazo (Kinyarwanda)…' :
            'Texte de la question (Français)…'
          }
          value={lang === 'en' ? text : lang === 'rw' ? textRw : textFr}
          onChange={e => {
            if (lang === 'en') setText(e.target.value);
            else if (lang === 'rw') setTextRw(e.target.value);
            else setTextFr(e.target.value);
          }}
        />
      </div>

      {/* Meta: weight, section, skip */}
      <div className='px-4 pb-3 flex items-center gap-4 flex-wrap text-xs text-gray-500'>
        <label className='flex items-center gap-1.5'>
          Weight
          <input type='number' min={0} value={weight} onChange={e => setWeight(Number(e.target.value))}
            className='w-14 text-sm border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-300' />
        </label>
        <label className='flex items-center gap-1.5'>
          Section
          <select value={sectionId} onChange={e => setSectionId(e.target.value)}
            className='text-sm border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300'>
            <option value=''>None</option>
            {sections.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        </label>
        {!question.parentQuestionId && skipOptionChoices.length > 0 && (
          <label className='flex items-center gap-1.5'>
            Hide if
            <select value={skipIfOptionId} onChange={e => setSkipIfOptionId(e.target.value)}
              className='text-sm border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300 max-w-[200px]'>
              <option value=''>— never —</option>
              {skipOptionChoices.map(c => (
                <option key={c.optionId} value={c.optionId}>{c.label}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      <hr className='border-gray-100 mx-4' />

      {/* Options */}
      <div className='px-4 py-3 space-y-1'>
        <p className='text-xs font-bold text-gray-400 uppercase tracking-wider mb-2'>Answer options</p>
        {localOptions.map((opt, i) => (
          <OptionEditRow
            key={opt.id ?? `new_${i}`}
            opt={opt}
            lang={lang}
            onChange={updated =>
              setLocalOptions(prev => prev.map((o, j) => j === i ? { ...updated, _dirty: !updated._new } : o))
            }
            onDelete={() => handleDeleteOption(i)}
            subQuestions={(question.subQuestions ?? []).filter(sq => sq.triggerOptionId === opt.id)}
            onAddSubQuestion={opt.id ? () => {
              const hygieneOpt = question.options?.find(o => o.id === opt.id);
              if (hygieneOpt) setAddingSubFor(hygieneOpt);
            } : undefined}
            questionnaireId={questionnaireId}
          />
        ))}
        <button type='button'
          onClick={() => setLocalOptions(prev => [...prev, { option: '', optionKinyarwanda: '', optionFrench: '', marks: 0, _dirty: false, _new: true }])}
          className='flex items-center gap-1.5 text-sm text-indigo-500 hover:text-indigo-700 mt-1'
        >
          <Plus className='h-4 w-4' /> Add option
        </button>
      </div>

      {/* Sub-question inline form */}
      {addingSubFor && (
        <div className='px-4 pb-3'>
          <SubQuestionForm
            parentQuestion={question}
            triggerOption={addingSubFor}
            onSave={body => createSubQMut.mutate(body)}
            onCancel={() => setAddingSubFor(null)}
            isPending={createSubQMut.isPending}
          />
        </div>
      )}

      {/* Footer */}
      <div className='px-4 py-3 border-t border-gray-100 flex items-center justify-between'>
        <button
          onClick={() => { if (window.confirm('Delete this question?')) deleteQMut.mutate(); }}
          disabled={deleteQMut.isPending}
          className='flex items-center gap-1.5 text-sm text-red-400 hover:text-red-600 disabled:opacity-50'
        >
          <Trash2 className='h-4 w-4' /> Delete question
        </button>
        <div className='flex items-center gap-2'>
          <button onClick={onDeactivate}
            className='px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50'>
            Cancel
          </button>
          <button onClick={handleSave} disabled={isSaving}
            className='px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium'>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── OptionEditRow ────────────────────────────────────────────────────────────

function OptionEditRow({
  opt, lang, onChange, onDelete, subQuestions, onAddSubQuestion, questionnaireId,
}: {
  opt: LocalOption;
  lang: LangKey;
  onChange: (updated: LocalOption) => void;
  onDelete: () => void;
  subQuestions: HygieneQuestionDto[];
  onAddSubQuestion?: () => void;
  questionnaireId: string;
}) {
  const textValue = lang === 'en' ? opt.option : lang === 'rw' ? opt.optionKinyarwanda : opt.optionFrench;
  const setTextValue = (v: string) => {
    if (lang === 'en') onChange({ ...opt, option: v });
    else if (lang === 'rw') onChange({ ...opt, optionKinyarwanda: v });
    else onChange({ ...opt, optionFrench: v });
  };

  return (
    <div className='space-y-1'>
      <div className='flex items-center gap-2 group/opt'>
        <span className='w-3 h-3 rounded-full border-2 border-indigo-300 shrink-0' />
        <input
          type='text' value={textValue} onChange={e => setTextValue(e.target.value)}
          placeholder={lang === 'en' ? 'Option text…' : lang === 'rw' ? 'Igisubizo…' : 'Option…'}
          className='flex-1 text-base border-0 border-b border-gray-200 focus:border-indigo-400 outline-none bg-transparent py-0.5 placeholder:text-gray-300'
        />
        <div className='flex items-center gap-1 shrink-0'>
          <input
            type='number' min={0} value={opt.marks}
            onChange={e => onChange({ ...opt, marks: Number(e.target.value) })}
            className='w-14 text-sm text-center border border-gray-200 rounded-lg px-1.5 py-0.5 focus:outline-none focus:border-indigo-300'
            title='Marks for this option'
          />
          <span className='text-xs text-gray-400'>pts</span>
          {onAddSubQuestion && (
            <button type='button' onClick={onAddSubQuestion} title='Add sub-question for this option'
              className='p-1 rounded hover:bg-purple-50 text-gray-300 hover:text-purple-500 opacity-0 group-hover/opt:opacity-100 transition-opacity'>
              <Plus className='h-3.5 w-3.5' />
            </button>
          )}
          <button type='button' onClick={onDelete} title='Remove option'
            className='p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover/opt:opacity-100 transition-opacity'>
            <Trash2 className='h-3.5 w-3.5' />
          </button>
        </div>
      </div>
      {subQuestions.length > 0 && (
        <div className='ml-5 pl-3 border-l-2 border-purple-100 space-y-1.5 py-1'>
          {subQuestions.map(sq => (
            <SubQuestionCard key={sq.id} subQuestion={sq} questionnaireId={questionnaireId} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SubQuestionCard ──────────────────────────────────────────────────────────

function SubQuestionCard({
  subQuestion,
  questionnaireId,
}: {
  subQuestion: HygieneQuestionDto;
  questionnaireId: string;
}) {
  const queryClient = useQueryClient();
  const queryKey = ['questions', 'hygiene', 'questionnaire', questionnaireId];
  const refresh = () => queryClient.invalidateQueries({ queryKey });

  const [isExpanded, setIsExpanded] = useState(false);
  const [lang, setLang] = useState<LangKey>('en');
  const [text, setText] = useState('');
  const [textRw, setTextRw] = useState('');
  const [textFr, setTextFr] = useState('');
  const [weight, setWeight] = useState(10);
  const [localOptions, setLocalOptions] = useState<LocalOption[]>([]);

  const enterEdit = () => {
    setText(subQuestion.question);
    setTextRw(subQuestion.questionKinyarwanda ?? '');
    setTextFr(subQuestion.questionFrench ?? '');
    setWeight(subQuestion.weight);
    setLocalOptions(optionsToLocal(subQuestion.options ?? []));
    setIsExpanded(true);
  };

  const updateQMut = useMutation({
    mutationFn: (body: UpdateHygieneQuestionDto) => questionsApi.updateHygieneQuestion(subQuestion.id, body),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });
  const deleteQMut = useMutation({
    mutationFn: () => questionsApi.deleteHygieneQuestion(subQuestion.id),
    onSuccess: () => { toast.success('Sub-question deleted'); refresh(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });
  const createOptMut = useMutation({
    mutationFn: (body: any) => questionsApi.createHygieneOption(subQuestion.id, body),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });
  const updateOptMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => questionsApi.updateHygieneOption(id, body),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });
  const deleteOptMut = useMutation({
    mutationFn: (id: string) => questionsApi.deleteHygieneOption(id),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const handleSave = async () => {
    if (!text.trim()) { toast.error('Question text required'); return; }
    try {
      await updateQMut.mutateAsync({
        question: text.trim(),
        questionKinyarwanda: textRw.trim() || undefined,
        questionFrench: textFr.trim() || undefined,
        weight,
      });
      for (const opt of localOptions) {
        if (opt._new && opt.option.trim()) {
          await createOptMut.mutateAsync({
            option: opt.option.trim(),
            optionKinyarwanda: opt.optionKinyarwanda.trim() || undefined,
            optionFrench: opt.optionFrench.trim() || undefined,
            marks: opt.marks,
          });
        } else if (!opt._new && opt._dirty && opt.id) {
          await updateOptMut.mutateAsync({
            id: opt.id,
            body: {
              option: opt.option.trim(),
              optionKinyarwanda: opt.optionKinyarwanda.trim() || undefined,
              optionFrench: opt.optionFrench.trim() || undefined,
              marks: opt.marks,
            },
          });
        }
      }
      toast.success('Saved');
      setIsExpanded(false);
      refresh();
    } catch { /* each mutation shows its own toast */ }
  };

  const handleDeleteOption = async (idx: number) => {
    const opt = localOptions[idx];
    if (opt._new) { setLocalOptions(prev => prev.filter((_, i) => i !== idx)); return; }
    if (!window.confirm('Delete this option?')) return;
    await deleteOptMut.mutateAsync(opt.id!);
    setLocalOptions(prev => prev.filter((_, i) => i !== idx));
    refresh();
  };

  const isSaving = updateQMut.isPending || createOptMut.isPending || updateOptMut.isPending;

  if (!isExpanded) {
    return (
      <div
        onClick={enterEdit}
        className='group flex items-center gap-2 text-sm text-purple-600 py-1 px-2 rounded-lg hover:bg-purple-50/60 cursor-pointer transition-colors'
      >
        <span className='text-purple-300 shrink-0'>↳</span>
        <span className='flex-1 truncate'>{subQuestion.question}</span>
        <span className='text-gray-400 shrink-0'>{subQuestion.options?.length ?? 0} opts</span>
        <span className='opacity-0 group-hover:opacity-100 text-purple-400 shrink-0 font-medium'>Edit</span>
      </div>
    );
  }

  return (
    <div className='border border-purple-200 rounded-xl bg-purple-50/40 p-3 space-y-2'>
      <div className='flex items-center justify-between'>
        <LangTabs active={lang} onChange={setLang} />
        <span className='text-xs font-semibold text-purple-500 uppercase tracking-wide'>Sub-question</span>
      </div>

      <textarea
        rows={2} autoFocus={lang === 'en'}
        value={lang === 'en' ? text : lang === 'rw' ? textRw : textFr}
        onChange={e => {
          if (lang === 'en') setText(e.target.value);
          else if (lang === 'rw') setTextRw(e.target.value);
          else setTextFr(e.target.value);
        }}
        className='w-full text-base border-0 border-b-2 border-purple-200 focus:border-purple-500 outline-none bg-transparent resize-none placeholder:text-gray-300'
        placeholder='Sub-question text…'
      />

      <label className='flex items-center gap-1.5 text-xs text-gray-500'>
        Weight
        <input type='number' min={0} value={weight} onChange={e => setWeight(Number(e.target.value))}
          className='w-14 text-sm border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-200' />
      </label>

      <div className='space-y-1'>
        <p className='text-xs font-bold text-purple-300 uppercase tracking-wider'>Options</p>
        {localOptions.map((opt, i) => (
          <div key={opt.id ?? `new_${i}`} className='flex items-center gap-2 group/sopt'>
            <span className='w-2.5 h-2.5 rounded-full border-2 border-purple-300 shrink-0' />
            <input type='text'
              value={lang === 'en' ? opt.option : lang === 'rw' ? opt.optionKinyarwanda : opt.optionFrench}
              onChange={e => {
                const v = e.target.value;
                setLocalOptions(prev => prev.map((o, j) => j !== i ? o :
                  lang === 'en' ? { ...o, option: v, _dirty: !o._new } :
                  lang === 'rw' ? { ...o, optionKinyarwanda: v, _dirty: !o._new } :
                  { ...o, optionFrench: v, _dirty: !o._new }
                ));
              }}
              placeholder='Option…'
              className='flex-1 text-sm border-0 border-b border-purple-100 focus:border-purple-300 outline-none bg-transparent py-0.5 placeholder:text-gray-300'
            />
            <input type='number' min={0} value={opt.marks}
              onChange={e => setLocalOptions(prev => prev.map((o, j) => j !== i ? o : { ...o, marks: Number(e.target.value), _dirty: !o._new }))}
              className='w-12 text-xs text-center border border-purple-200 rounded px-1 py-0.5 focus:outline-none' />
            <span className='text-xs text-gray-400'>pts</span>
            <button type='button' onClick={() => handleDeleteOption(i)}
              className='p-0.5 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover/sopt:opacity-100 transition-opacity'>
              <Trash2 className='h-3 w-3' />
            </button>
          </div>
        ))}
        <button type='button'
          onClick={() => setLocalOptions(prev => [...prev, { option: '', optionKinyarwanda: '', optionFrench: '', marks: 0, _dirty: false, _new: true }])}
          className='flex items-center gap-1 text-xs text-purple-500 hover:text-purple-700 mt-1'>
          <Plus className='h-3 w-3' /> Add option
        </button>
      </div>

      <div className='flex items-center justify-between pt-1'>
        <button type='button'
          onClick={() => { if (window.confirm('Delete this sub-question?')) deleteQMut.mutate(); }}
          disabled={deleteQMut.isPending}
          className='flex items-center gap-1 text-xs text-red-400 hover:text-red-600 disabled:opacity-50'>
          <Trash2 className='h-3 w-3' /> Delete
        </button>
        <div className='flex gap-2'>
          <button type='button' onClick={() => setIsExpanded(false)}
            className='px-2.5 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50'>
            Cancel
          </button>
          <button type='button' onClick={handleSave} disabled={isSaving}
            className='px-3 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium'>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── NewQuestionCard ──────────────────────────────────────────────────────────

function NewQuestionCard({
  questionnaireId, sectionId, sections, skipOptionChoices, onSaved, onCancel,
}: {
  questionnaireId: string;
  sectionId: string | null;
  sections: QuestionnaireSectionDto[];
  skipOptionChoices: { optionId: string; label: string }[];
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [lang, setLang] = useState<LangKey>('en');
  const [text, setText] = useState('');
  const [textRw, setTextRw] = useState('');
  const [textFr, setTextFr] = useState('');
  const [weight, setWeight] = useState(10);
  const [selectedSectionId, setSelectedSectionId] = useState(sectionId ?? '');
  const [skipIfOptionId, setSkipIfOptionId] = useState('');
  const [localOptions, setLocalOptions] = useState<LocalOption[]>([
    { option: 'Yes', optionKinyarwanda: 'Yego', optionFrench: 'Oui', marks: 10, _dirty: false, _new: true },
    { option: 'No', optionKinyarwanda: 'Oya', optionFrench: 'Non', marks: 0, _dirty: false, _new: true },
  ]);

  const mutation = useMutation({
    mutationFn: (body: CreateHygieneQuestionDto) => questionsApi.createHygieneQuestion(questionnaireId, body),
    onSuccess: () => { toast.success('Question added'); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  const handleSave = () => {
    if (!text.trim()) { toast.error('Question text is required'); return; }
    mutation.mutate({
      question: text.trim(),
      questionKinyarwanda: textRw.trim() || undefined,
      questionFrench: textFr.trim() || undefined,
      weight,
      sectionId: selectedSectionId || undefined,
      skipIfOptionId: skipIfOptionId || undefined,
      options: localOptions.filter(o => o.option.trim()).map(o => ({
        option: o.option.trim(),
        optionKinyarwanda: o.optionKinyarwanda.trim() || undefined,
        optionFrench: o.optionFrench.trim() || undefined,
        marks: o.marks,
      })),
    });
  };

  return (
    <div className='border-2 border-dashed border-indigo-300 rounded-xl bg-white shadow-sm'>
      <div className='px-4 pt-4 pb-2 flex items-center justify-between'>
        <LangTabs active={lang} onChange={setLang} />
        <span className='text-xs font-bold text-indigo-500 uppercase tracking-wide'>New question</span>
      </div>

      <div className='px-4 pb-3'>
        <textarea
          rows={2} autoFocus
          className='w-full text-base font-medium border-0 border-b-2 border-indigo-200 focus:border-indigo-500 outline-none resize-none bg-transparent pb-1 transition-colors placeholder:text-gray-300'
          placeholder={
            lang === 'en' ? 'Question text (English)…' :
            lang === 'rw' ? 'Ibibazo (Kinyarwanda)…' :
            'Texte de la question (Français)…'
          }
          value={lang === 'en' ? text : lang === 'rw' ? textRw : textFr}
          onChange={e => {
            if (lang === 'en') setText(e.target.value);
            else if (lang === 'rw') setTextRw(e.target.value);
            else setTextFr(e.target.value);
          }}
        />
      </div>

      <div className='px-4 pb-3 flex items-center gap-4 flex-wrap text-xs text-gray-500'>
        <label className='flex items-center gap-1.5'>
          Weight
          <input type='number' min={0} value={weight} onChange={e => setWeight(Number(e.target.value))}
            className='w-14 text-sm border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-300' />
        </label>
        <label className='flex items-center gap-1.5'>
          Section
          <select value={selectedSectionId} onChange={e => setSelectedSectionId(e.target.value)}
            className='text-sm border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300'>
            <option value=''>None</option>
            {sections.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        </label>
        {skipOptionChoices.length > 0 && (
          <label className='flex items-center gap-1.5'>
            Hide if
            <select value={skipIfOptionId} onChange={e => setSkipIfOptionId(e.target.value)}
              className='text-sm border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-300 max-w-[200px]'>
              <option value=''>— never —</option>
              {skipOptionChoices.map(c => <option key={c.optionId} value={c.optionId}>{c.label}</option>)}
            </select>
          </label>
        )}
      </div>

      <hr className='border-gray-100 mx-4' />

      <div className='px-4 py-3 space-y-1'>
        <p className='text-xs font-bold text-gray-400 uppercase tracking-wider mb-2'>Answer options</p>
        {localOptions.map((opt, i) => (
          <div key={i} className='flex items-center gap-2 group/nopt'>
            <span className='w-3 h-3 rounded-full border-2 border-indigo-300 shrink-0' />
            <input
              type='text'
              value={lang === 'en' ? opt.option : lang === 'rw' ? opt.optionKinyarwanda : opt.optionFrench}
              onChange={e => {
                const v = e.target.value;
                setLocalOptions(prev => prev.map((o, j) => j !== i ? o :
                  lang === 'en' ? { ...o, option: v } :
                  lang === 'rw' ? { ...o, optionKinyarwanda: v } :
                  { ...o, optionFrench: v }
                ));
              }}
              placeholder={lang === 'en' ? 'Option text…' : lang === 'rw' ? 'Igisubizo…' : 'Option…'}
              className='flex-1 text-base border-0 border-b border-gray-200 focus:border-indigo-400 outline-none bg-transparent py-0.5 placeholder:text-gray-300'
            />
            <input type='number' min={0} value={opt.marks}
              onChange={e => setLocalOptions(prev => prev.map((o, j) => j !== i ? o : { ...o, marks: Number(e.target.value) }))}
              className='w-14 text-sm text-center border border-gray-200 rounded-lg px-1.5 py-0.5 focus:outline-none focus:border-indigo-300'
              title='Marks' />
            <span className='text-xs text-gray-400'>pts</span>
            <button type='button' onClick={() => setLocalOptions(prev => prev.filter((_, j) => j !== i))}
              className='p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover/nopt:opacity-100 transition-opacity'>
              <Trash2 className='h-3.5 w-3.5' />
            </button>
          </div>
        ))}
        <button type='button'
          onClick={() => setLocalOptions(prev => [...prev, { option: '', optionKinyarwanda: '', optionFrench: '', marks: 0, _dirty: false, _new: true }])}
          className='flex items-center gap-1.5 text-sm text-indigo-500 hover:text-indigo-700 mt-1'>
          <Plus className='h-4 w-4' /> Add option
        </button>
      </div>

      <div className='px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2'>
        <button onClick={onCancel} className='px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50'>
          Cancel
        </button>
        <button onClick={handleSave} disabled={mutation.isPending}
          className='px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium'>
          {mutation.isPending ? 'Adding…' : 'Add question'}
        </button>
      </div>
    </div>
  );
}

// ─── SubQuestionForm ──────────────────────────────────────────────────────────

function SubQuestionForm({
  parentQuestion, triggerOption, onSave, onCancel, isPending,
}: {
  parentQuestion: HygieneQuestionDto;
  triggerOption: HygieneOptionDto;
  onSave: (body: CreateHygieneQuestionDto) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [lang, setLang] = useState<LangKey>('en');
  const [text, setText] = useState('');
  const [textRw, setTextRw] = useState('');
  const [textFr, setTextFr] = useState('');
  const [weight, setWeight] = useState(10);
  const [options, setOptions] = useState([
    { option: 'Yes', optionKinyarwanda: 'Yego', optionFrench: 'Oui', marks: 10 },
    { option: 'No', optionKinyarwanda: 'Oya', optionFrench: 'Non', marks: 0 },
  ]);

  const handleSave = () => {
    if (!text.trim()) { toast.error('Question text required'); return; }
    onSave({
      question: text.trim(),
      questionKinyarwanda: textRw.trim() || undefined,
      questionFrench: textFr.trim() || undefined,
      weight,
      parentQuestionId: parentQuestion.id,
      triggerOptionId: triggerOption.id,
      options: options.filter(o => o.option.trim()).map(o => ({
        option: o.option.trim(),
        optionKinyarwanda: o.optionKinyarwanda.trim() || undefined,
        optionFrench: o.optionFrench.trim() || undefined,
        marks: o.marks,
      })),
    });
  };

  return (
    <div className='border border-purple-200 rounded-xl bg-purple-50/40 p-3 space-y-2 mt-2'>
      <div className='flex items-center justify-between flex-wrap gap-2'>
        <div className='flex items-center gap-2 text-xs text-gray-500'>
          <span className='font-semibold text-purple-700'>Sub-question</span>
          <span>· shown when answer is</span>
          <span className='font-medium text-gray-800 bg-white border border-gray-200 rounded px-1.5 py-0.5'>
            "{triggerOption.option}"
          </span>
        </div>
        <LangTabs active={lang} onChange={setLang} />
      </div>

      <textarea
        rows={2} autoFocus
        value={lang === 'en' ? text : lang === 'rw' ? textRw : textFr}
        onChange={e => {
          if (lang === 'en') setText(e.target.value);
          else if (lang === 'rw') setTextRw(e.target.value);
          else setTextFr(e.target.value);
        }}
        placeholder='Sub-question text…'
        className='w-full text-base border-0 border-b-2 border-purple-200 focus:border-purple-500 outline-none bg-transparent resize-none placeholder:text-gray-300'
      />

      <label className='flex items-center gap-1.5 text-xs text-gray-500'>
        Weight
        <input type='number' min={0} value={weight} onChange={e => setWeight(Number(e.target.value))}
          className='w-14 text-sm border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-200' />
      </label>

      <div className='space-y-1'>
        {options.map((opt, i) => (
          <div key={i} className='flex items-center gap-2'>
            <span className='w-2.5 h-2.5 rounded-full border-2 border-purple-300 shrink-0' />
            <input type='text'
              value={lang === 'en' ? opt.option : lang === 'rw' ? opt.optionKinyarwanda : opt.optionFrench}
              onChange={e => {
                const v = e.target.value;
                setOptions(prev => prev.map((o, j) => j !== i ? o :
                  lang === 'en' ? { ...o, option: v } :
                  lang === 'rw' ? { ...o, optionKinyarwanda: v } :
                  { ...o, optionFrench: v }
                ));
              }}
              placeholder='Option…'
              className='flex-1 text-sm border-0 border-b border-purple-100 focus:border-purple-300 outline-none bg-transparent py-0.5 placeholder:text-gray-300'
            />
            <input type='number' min={0} value={opt.marks}
              onChange={e => setOptions(prev => prev.map((o, j) => j !== i ? o : { ...o, marks: Number(e.target.value) }))}
              className='w-12 text-xs text-center border border-purple-200 rounded px-1 py-0.5 focus:outline-none' />
            <span className='text-xs text-gray-400'>pts</span>
          </div>
        ))}
        <button type='button'
          onClick={() => setOptions(prev => [...prev, { option: '', optionKinyarwanda: '', optionFrench: '', marks: 0 }])}
          className='flex items-center gap-1 text-xs text-purple-500 hover:text-purple-700 mt-1'>
          <Plus className='h-3 w-3' /> Add option
        </button>
      </div>

      <div className='flex items-center justify-end gap-2 pt-1'>
        <button type='button' onClick={onCancel}
          className='px-2.5 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50'>
          Cancel
        </button>
        <button type='button' onClick={handleSave} disabled={isPending}
          className='px-3 py-1 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium'>
          {isPending ? 'Adding…' : 'Add sub-question'}
        </button>
      </div>
    </div>
  );
}

// ─── SectionEditor ────────────────────────────────────────────────────────────

function SectionEditor({
  section, subcategories, isPending, onSubmit, onCancel,
}: {
  section: QuestionnaireSectionDto | null;
  subcategories: FacilitySubcategoryDto[];
  isPending: boolean;
  onSubmit: (body: any) => void;
  onCancel: () => void;
}) {
  const [lang, setLang] = useState<LangKey>('en');
  const [title, setTitle] = useState(section?.title ?? '');
  const [titleRw, setTitleRw] = useState(section?.titleKinyarwanda ?? '');
  const [titleFr, setTitleFr] = useState(section?.titleFrench ?? '');
  const [description, setDescription] = useState(section?.description ?? '');
  const [order, setOrder] = useState<number | ''>(section?.order ?? 0);
  const [selectedSubIds, setSelectedSubIds] = useState<string[]>(section?.applicableSubcategoryIds ?? []);

  const titleValue = lang === 'en' ? title : lang === 'rw' ? titleRw : titleFr;
  const setTitleValue = (v: string) => {
    if (lang === 'en') setTitle(v);
    else if (lang === 'rw') setTitleRw(v);
    else setTitleFr(v);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Section title required'); return; }
    onSubmit({
      title: title.trim(),
      titleKinyarwanda: titleRw.trim() || undefined,
      titleFrench: titleFr.trim() || undefined,
      description: description.trim() || undefined,
      order: order === '' ? undefined : Number(order),
      applicableSubcategoryIds: selectedSubIds.length ? selectedSubIds : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className='border-2 border-indigo-300 rounded-xl bg-indigo-50/30 p-4 space-y-3 mt-6'>
      <div className='flex items-center justify-between'>
        <LangTabs active={lang} onChange={setLang} />
        <span className='text-xs font-bold text-indigo-600 uppercase tracking-wide'>
          {section ? 'Edit section' : 'New section'}
        </span>
      </div>

      <input
        required autoFocus
        value={titleValue} onChange={e => setTitleValue(e.target.value)}
        placeholder={lang === 'en' ? 'Section title (English)…' : lang === 'rw' ? 'Inyito (Kinyarwanda)…' : 'Titre (Français)…'}
        className='w-full text-sm font-bold border-0 border-b-2 border-indigo-300 focus:border-indigo-600 outline-none bg-transparent pb-1 text-indigo-900 placeholder:text-indigo-300'
      />

      <div className='flex items-center gap-4 flex-wrap text-xs text-gray-500'>
        <label className='flex items-center gap-1.5'>
          Description
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder='Optional'
            className='text-sm border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-300' />
        </label>
        <label className='flex items-center gap-1.5'>
          Order
          <input type='number' min={0} value={order === '' ? '' : order}
            onChange={e => setOrder(e.target.value === '' ? '' : Number(e.target.value))}
            className='w-16 text-sm border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-300' />
        </label>
      </div>

      {subcategories.length > 0 && (
        <div>
          <p className='text-xs text-gray-500 mb-2'>Applies to subcategories <span className='text-gray-400'>(leave empty = all)</span></p>
          <div className='flex flex-wrap gap-2'>
            {subcategories.map(sub => (
              <label
                key={sub.id}
                className={cn(
                  'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs cursor-pointer border transition-colors select-none',
                  selectedSubIds.includes(sub.id)
                    ? 'bg-indigo-100 border-indigo-400 text-indigo-800'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-indigo-300'
                )}
              >
                <input
                  type='checkbox' className='sr-only'
                  checked={selectedSubIds.includes(sub.id)}
                  onChange={() => setSelectedSubIds(prev =>
                    prev.includes(sub.id) ? prev.filter(x => x !== sub.id) : [...prev, sub.id]
                  )}
                />
                {sub.name}
              </label>
            ))}
          </div>
        </div>
      )}

      <div className='flex items-center justify-end gap-2 pt-1'>
        <button type='button' onClick={onCancel} className='px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50'>
          Cancel
        </button>
        <button type='submit' disabled={isPending}
          className='px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium'>
          {isPending ? 'Saving…' : section ? 'Update section' : 'Add section'}
        </button>
      </div>
    </form>
  );
}
